import * as Sentry from '@sentry/react';
import { trackPendoEvent } from 'services/PendoService';
import type { FrameOrderValve, OutOfOrderFrame } from 'utils/frameOrder';

/**
 * EN-11178 / EN-11182: report out-of-order table frames (utils/frameOrder.ts),
 * so how often they reach players -- and, with release.table-frame-order on,
 * how often they are dropped -- can be counted through the dropper and
 * PACECHAIN rollouts. Sent only behind ops.frame-order-report.
 *
 * To Pendo, which is live in production builds, and to Sentry, which only
 * reports where REACT_APP_SENTRY_DSN is set (no shipped build, today). Pendo is
 * the signal the rollout reads; it needs the identified player, so a guest
 * sends nothing there.
 *
 * Bounded so a table in trouble cannot flood either: at most one out-of-order
 * report per table per hand (the newest frame's hand, which is what the player
 * was looking at), one valve report per table, and MAX_REPORTS_PER_SESSION in
 * all. The rest are still counted on the QA Performance HUD.
 */
export const MAX_REPORTS_PER_SESSION = 20;

const reported = new Set<string>();

/** Claims a report slot for `key`; false when it was taken or the cap is reached. */
const claim = (key: string): boolean => {
  if (reported.has(key) || reported.size >= MAX_REPORTS_PER_SESSION) return false;
  reported.add(key);
  return true;
};

type Facts = Record<string, string | number | boolean>;

const send = (
  message: string,
  eventName: string,
  key: string,
  playerGUID: string,
  tags: Facts,
  facts: Facts
) => {
  // App.tsx sets the player's email as the Sentry user for the whole app; these
  // events carry table facts only, so they are sent with that user cleared.
  Sentry.withScope((scope) => {
    scope.setUser(null);
    scope.setLevel('warning');
    // Sentry tags are strings; Pendo keeps each value's own type, so the
    // rollout can filter `crossHand` and `dropped` as booleans.
    const sentryTags = Object.fromEntries(Object.entries(tags).map(([k, v]) => [k, String(v)]));
    scope.setTags({ area: 'frame-order', ...sentryTags });
    scope.setExtras(facts);
    Sentry.captureMessage(message);
  });
  if (playerGUID) {
    trackPendoEvent({
      playerGUID,
      eventName,
      properties: { ...tags, ...facts },
      dedupeKey: `${playerGUID}:${eventName}:${key}`
    });
  }
};

export const reportOutOfOrderFrame = (frame: OutOfOrderFrame, playerGUID = ''): boolean => {
  const key = `${frame.gameID}:${frame.newest.gameNo}`;
  if (!claim(key)) return false;
  send(
    'Table frame arrived out of order',
    'table_frame_out_of_order',
    key,
    playerGUID,
    { crossHand: frame.crossHand, dropped: frame.dropped },
    {
      gameID: frame.gameID,
      behindMs: frame.behindMs,
      frameGameNo: frame.frame.gameNo,
      frameHand: frame.frame.hand,
      newestGameNo: frame.newest.gameNo,
      newestHand: frame.newest.hand
    }
  );
  return true;
};

/**
 * The valve opened: a frame read older than the mark showed the table further
 * along, so the mark was reset. Worth a look every time -- it means the mark was
 * wrong (the database clock stepped back), not that frames were late.
 */
export const reportFrameOrderValve = (valve: FrameOrderValve, playerGUID = ''): boolean => {
  const key = `${valve.gameID}:valve`;
  if (!claim(key)) return false;
  send(
    'Table frame order valve opened',
    'table_frame_order_valve',
    key,
    playerGUID,
    { valve: 'opened' },
    { gameID: valve.gameID, dropped: valve.dropped, behindMs: valve.behindMs }
  );
  return true;
};

/** Tests only. */
export const resetFrameOrderReports = () => {
  reported.clear();
};
